import { useCallback, useMemo, useState } from 'react'

import type { CampaignContactRow } from '#/entities/campaign'

import { CONTACT_DIALOGS } from './use-contact-dialog'
import type { ContactDialog } from './use-contact-dialog'

export interface CampaignContactRowActions {
  onOpenInPhone: (row: CampaignContactRow) => void
  onAddToSequence: (row: CampaignContactRow) => void
  onAssign: (row: CampaignContactRow) => void
  onCreateTask: (row: CampaignContactRow) => void
  onRemove: (row: CampaignContactRow) => void
  busy: boolean
}

interface UseCampaignContactRowActionsArgs {
  rows: CampaignContactRow[]
  selectedIds: string[]
  onOpenRowInPhone: (row: CampaignContactRow) => void
  onRemoveRow: (row: CampaignContactRow) => void
  openDialog: (dialog: ContactDialog) => void
  closeDialog: () => void
  busy: boolean
}

export interface UseCampaignContactRowActionsResult {
  actions: CampaignContactRowActions
  /** Who the open dialog acts on — the row a menu aimed it at, or the ticks. */
  actionRows: CampaignContactRow[]
  actionIds: string[]
  /** Opens a bulk-bar dialog, dropping any row a menu had aimed at. */
  openBulk: (dialog: ContactDialog) => () => void
  onDialogClose: () => void
}

/**
 * The Contact tab's row menu, and who its dialogs act on.
 *
 * Three of its items reuse the bulk bar's dialogs, which act on whoever is
 * ticked — so opening one from a row first parks that row and the tab
 * substitutes it for the selection. Without that, a menu opened on Maria while
 * three other people were ticked would assign the other three and leave Maria
 * alone, which is the kind of bug that reaches a customer.
 */
export function useCampaignContactRowActions({
  rows,
  selectedIds,
  onOpenRowInPhone,
  onRemoveRow,
  openDialog,
  closeDialog,
  busy,
}: UseCampaignContactRowActionsArgs): UseCampaignContactRowActionsResult {
  const [rowTarget, setRowTarget] = useState<CampaignContactRow | null>(null)

  const aim = useCallback(
    (dialog: ContactDialog) => (row: CampaignContactRow) => {
      setRowTarget(row)
      openDialog(dialog)
    },
    [openDialog],
  )

  const actions = useMemo<CampaignContactRowActions>(
    () => ({
      onOpenInPhone: onOpenRowInPhone,
      onAddToSequence: aim(CONTACT_DIALOGS.add),
      onAssign: aim(CONTACT_DIALOGS.assign),
      onCreateTask: aim(CONTACT_DIALOGS.tasks),
      onRemove: onRemoveRow,
      busy,
    }),
    [onOpenRowInPhone, onRemoveRow, aim, busy],
  )

  const actionRows = useMemo(
    () =>
      rowTarget !== null
        ? [rowTarget]
        : rows.filter((row) => selectedIds.includes(row.id)),
    [rowTarget, rows, selectedIds],
  )

  const openBulk = useCallback(
    (dialog: ContactDialog) => () => {
      setRowTarget(null)
      openDialog(dialog)
    },
    [openDialog],
  )

  const onDialogClose = useCallback(() => {
    setRowTarget(null)
    closeDialog()
  }, [closeDialog])

  return {
    actions,
    actionRows,
    actionIds: actionRows.map((row) => row.id),
    openBulk,
    onDialogClose,
  }
}
